import { SpecPackage } from '../types/spec';
import { BENCHMARK_FULL_DOCS } from './BenchmarkData';
import { ASTParserService } from './ASTParserService';
import { FileSystemIngestion } from './FileSystemIngestion';

export interface BenchmarkPreset {
  id: string;
  name: string;
  repoOwner: string;
  repoName: string;
  description: string;
  docCount: number;
}

export const BENCHMARK_PRESETS: BenchmarkPreset[] = [
  {
    id: 'postmq',
    name: 'PostMQ',
    repoOwner: 'No-Compromise-AI',
    repoName: 'PostMQ',
    description: 'Durable messaging platform for autonomous AI agents & accounts.',
    docCount: 25
  },
  {
    id: 'inxo',
    name: 'inxo',
    repoOwner: 'No-Compromise-AI',
    repoName: 'inxo',
    description: 'AI-assisted inbox, email connector, identity & security specification suite.',
    docCount: 42
  },
  {
    id: 'adplaced',
    name: 'AdPlaced',
    repoOwner: 'No-Compromise-AI',
    repoName: 'AdPlaced',
    description: 'High-density ad placement, auction telemetry & integration architecture spec.',
    docCount: 26
  }
];

export class BenchmarkService {
  public static getPresets(): BenchmarkPreset[] {
    return BENCHMARK_PRESETS;
  }

  public static async loadPreset(presetId: string): Promise<SpecPackage> {
    const preset = BENCHMARK_PRESETS.find(p => p.id === presetId);
    if (!preset) {
      throw new Error(`Unknown benchmark preset: ${presetId}`);
    }

    // Attempt browser zip fetch if in browser environment
    if (typeof window !== 'undefined' && typeof fetch === 'function' && window.location?.protocol?.startsWith('http')) {
      try {
        const res = await fetch(`/sample-specs/${preset.name}-spec.zip`);
        if (res.ok) {
          const blob = await res.blob();
          const file = new File([blob], `${preset.name}-spec.zip`, { type: 'application/zip' });
          const { textFiles, assets } = await FileSystemIngestion.readZipPackage(file);
          if (textFiles.length > 0) {
            const parsedDocs = ASTParserService.parseFileEntries(textFiles);
            const allTasks = parsedDocs.flatMap(d => d.tasks);
            return {
              id: `preset-${preset.id}`,
              name: preset.name,
              sourceType: 'preset',
              sourceUrl: `https://github.com/${preset.repoOwner}/${preset.repoName}/tree/main/docs`,
              loadedAt: new Date().toISOString(),
              documents: parsedDocs,
              assets,
              taskMatrix: allTasks,
              dagEdges: parsedDocs.slice(0, -1).map((d, i) => ({
                source: d.name,
                target: parsedDocs[i + 1].name,
                relationship: 'references'
              }))
            };
          }
        }
      } catch (e) {
        console.warn(`Browser zip fetch failed for preset ${preset.name}:`, e);
      }
    }

    // Static embedded cache fallback
    const rawFilesDict = BENCHMARK_FULL_DOCS[preset.id.toLowerCase()] || {};
    const textFiles = Object.entries(rawFilesDict)
      .filter(([path]) => ['md', 'markdown', 'mdx', 'json', 'yaml', 'yml'].some(ext => path.endsWith(`.${ext}`)))
      .map(([path, content]) => {
        const pathParts = path.split('/');
        const name = pathParts.pop() || path;
        const folderPath = pathParts.join('/') || 'root';
        return {
          path,
          name,
          folderPath,
          content
        };
      });

    const parsedDocs = ASTParserService.parseFileEntries(textFiles);
    const allTasks = parsedDocs.flatMap(d => d.tasks);

    return {
      id: `preset-${preset.id}`,
      name: preset.name,
      sourceType: 'preset',
      sourceUrl: `https://github.com/${preset.repoOwner}/${preset.repoName}/tree/main/docs`,
      loadedAt: new Date().toISOString(),
      documents: parsedDocs,
      taskMatrix: allTasks,
      dagEdges: parsedDocs.slice(0, -1).map((d, i) => ({
        source: d.name,
        target: parsedDocs[i + 1].name,
        relationship: 'references'
      }))
    };
  }
}
